/**
 * SupporterProfilePanel — role-specific profile editor for the four supporter
 * personas. Basic identity (name, bio, phone) is saved via PUT /users/me (and
 * mirrored into the auth store); role-specific extras are saved via
 * PUT /supporters/profile. NO bank / payment / donation / financial fields.
 *
 * Inline form (no modal) — fully responsive, no overlay/overlap risk.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, CheckCircle2 } from 'lucide-react'
import { GovCard, GovButton } from '@/components/layout/GovLayout'
import { useAuthStore } from '@/stores/authStore'
import userService from '@/services/userService'
import {
  getMySupporterProfile,
  updateMySupporterProfile,
  type SupporterProfile,
} from '@/services/supporterProfileService'

export type SupporterRole = 'individual' | 'ngo' | 'corporate' | 'nutrition'

interface FieldDef {
  key: keyof SupporterProfile
  labelKey: string
  fallback: string
  type?: 'text' | 'textarea'
  placeholder?: string
}

const ORG_FIELDS: FieldDef[] = [
  { key: 'organizationName', labelKey: 'supporter.profile.organizationName', fallback: 'Organisation name' },
  { key: 'logoUrl', labelKey: 'supporter.profile.logoUrl', fallback: 'Logo URL' },
  { key: 'industry', labelKey: 'supporter.profile.industry', fallback: 'Industry' },
  { key: 'areasOfOperation', labelKey: 'supporter.profile.areasOfOperation', fallback: 'Areas of operation' },
  { key: 'focusAreas', labelKey: 'supporter.profile.focusAreas', fallback: 'Focus areas' },
  { key: 'website', labelKey: 'supporter.profile.website', fallback: 'Website' },
  { key: 'contactPerson', labelKey: 'supporter.profile.contactPerson', fallback: 'Contact person' },
  { key: 'description', labelKey: 'supporter.profile.description', fallback: 'Organisation description', type: 'textarea' },
]

const COMMON_FIELDS: FieldDef[] = [
  { key: 'location', labelKey: 'supporter.profile.location', fallback: 'Location' },
  { key: 'preferredLanguage', labelKey: 'supporter.profile.preferredLanguage', fallback: 'Preferred language' },
  { key: 'awarenessInterests', labelKey: 'supporter.profile.awarenessInterests', fallback: 'Awareness interests', type: 'textarea' },
  { key: 'nutritionInterests', labelKey: 'supporter.profile.nutritionInterests', fallback: 'Nutrition interests', type: 'textarea' },
  { key: 'communityPreferences', labelKey: 'supporter.profile.communityPreferences', fallback: 'Community preferences', type: 'textarea' },
]

function fieldsForRole(role: SupporterRole): FieldDef[] {
  const orgKeys = new Set<keyof SupporterProfile>(
    role === 'corporate'
      ? ['organizationName', 'logoUrl', 'industry', 'areasOfOperation', 'website', 'contactPerson', 'description']
      : role === 'ngo'
        ? ['organizationName', 'logoUrl', 'areasOfOperation', 'focusAreas', 'website', 'contactPerson', 'description']
        : [],
  )
  const commonKeys = new Set<keyof SupporterProfile>(
    role === 'nutrition'
      ? ['location', 'preferredLanguage', 'nutritionInterests', 'awarenessInterests', 'communityPreferences']
      : role === 'individual'
        ? ['location', 'preferredLanguage', 'awarenessInterests', 'communityPreferences']
        : ['location', 'preferredLanguage', 'awarenessInterests', 'communityPreferences'],
  )
  return [
    ...ORG_FIELDS.filter((f) => orgKeys.has(f.key)),
    ...COMMON_FIELDS.filter((f) => commonKeys.has(f.key)),
  ]
}

const inputClass = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]'

export function SupporterProfilePanel({ role }: { role: SupporterRole }) {
  const { t } = useTranslation()
  const { user, updateProfile } = useAuthStore()

  const [profile, setProfile] = useState<SupporterProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Basic identity fields
  const [firstName, setFirstName] = useState(user?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.lastName ?? '')
  const [bio, setBio] = useState(user?.bio ?? '')
  const [phone, setPhone] = useState(user?.phone ?? '')

  const fields = fieldsForRole(role)

  useEffect(() => {
    let active = true
    setLoading(true)
    getMySupporterProfile()
      .then((p) => { if (active) setProfile(p) })
      .catch(() => { if (active) setProfile(null) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [role])

  const setRoleField = (key: keyof SupporterProfile, value: string) => {
    setProfile((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      // 1) Role-specific profile
      if (profile) {
        const rolePatch: Record<string, string> = {}
        for (const f of fields) rolePatch[f.key] = (profile[f.key] as string) ?? ''
        await updateMySupporterProfile(rolePatch)
      }
      // 2) Basic identity (only if something changed)
      const basicChanged =
        firstName !== (user?.firstName ?? '') ||
        lastName !== (user?.lastName ?? '') ||
        bio !== (user?.bio ?? '') ||
        phone !== (user?.phone ?? '')
      if (basicChanged) {
        const updated = await userService.updateProfile({ firstName, lastName, bio, phone })
        updateProfile(updated)
      }
      setSaved(true)
    } catch (err: any) {
      setError(err?.response?.data?.message || t('supporter.profile.error', 'Could not save. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <GovCard>
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-12 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      </GovCard>
    )
  }

  return (
    <div className="space-y-5">
      <GovCard>
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          {t('supporter.profile.basicTitle', 'Basic details')}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {t('supporter.profile.basicDesc', 'Your name and contact help us personalise your awareness journey.')}
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="sp-first" className="block text-sm font-medium text-gray-700 mb-1">{t('supporter.profile.firstName', 'First name')}</label>
            <input id="sp-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="sp-last" className="block text-sm font-medium text-gray-700 mb-1">{t('supporter.profile.lastName', 'Last name')}</label>
            <input id="sp-last" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="sp-phone" className="block text-sm font-medium text-gray-700 mb-1">{t('supporter.profile.phone', 'Phone')}</label>
            <input id="sp-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="sp-bio" className="block text-sm font-medium text-gray-700 mb-1">{t('supporter.profile.bio', 'Short bio')}</label>
            <textarea id="sp-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={2} className={`${inputClass} resize-y`} />
          </div>
        </div>
      </GovCard>

      {fields.length > 0 && (
        <GovCard>
          <h3 className="text-base font-semibold text-gray-900 mb-1">
            {t('supporter.profile.roleTitle', 'Role details')}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('supporter.profile.roleDesc', 'Help us recommend relevant awareness content and collaborations.')}
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                <label htmlFor={`sp-${f.key}`} className="block text-sm font-medium text-gray-700 mb-1">
                  {t(f.labelKey, f.fallback)}
                </label>
                {f.type === 'textarea' ? (
                  <textarea
                    id={`sp-${f.key}`}
                    value={(profile?.[f.key] as string) ?? ''}
                    onChange={(e) => setRoleField(f.key, e.target.value)}
                    rows={3}
                    className={`${inputClass} resize-y`}
                  />
                ) : (
                  <input
                    id={`sp-${f.key}`}
                    value={(profile?.[f.key] as string) ?? ''}
                    onChange={(e) => setRoleField(f.key, e.target.value)}
                    className={inputClass}
                  />
                )}
              </div>
            ))}
          </div>
        </GovCard>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
      {saved && (
        <p className="text-sm text-emerald-600 flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" />
          {t('supporter.profile.saved', 'Profile saved.')}
        </p>
      )}
      <GovButton variant="primary" onClick={handleSave} className="inline-flex items-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        {t('supporter.profile.save', 'Save profile')}
      </GovButton>
    </div>
  )
}